import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useFetch, CHART_COLORS } from "@/lib/shieldx";
import { PageHeader, Panel, Loader, Stat } from "@/components/shieldx/Primitives";
import { DemoTag } from "@/components/shieldx/Badges";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Activity } from "lucide-react";

const ttStyle = { background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12, fontFamily: "JetBrains Mono" };
const SEV_COLORS = { LOW: "#10B981", MEDIUM: "#F59E0B", HIGH: "#FB923C", CRITICAL: "#EF4444" };

export default function SOC() {
  const { data, loading } = useFetch("/dashboard/soc");
  if (loading || !data) return <Loader label="Loading security operations" />;
  const m = data.metrics;

  return (
    <div>
      <PageHeader icon={Activity} title="Security Operations"
        subtitle="SOC analytics — detection, containment and recovery performance." actions={<DemoTag />} />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="MTTD" value={m.mttd} tone="cyan" sub="Mean time to detect" />
        <Stat label="MTTC" value={m.mttc} tone="amber" sub="Mean time to contain" />
        <Stat label="MTTR" value={m.mttr} tone="blue" sub="Mean time to recover" />
        <Stat label="Protection Coverage" value={`${m.protection_coverage}%`} tone="emerald" />
        <Stat label="Active Incidents" value={m.active_incidents} tone="amber" />
        <Stat label="Critical" value={m.critical} tone="red" />
        <Stat label="AI Incidents" value={m.ai_incidents} tone="purple" />
        <Stat label="Incident States" value={data.incident_states.length} tone="slate" sub="Distinct workflow states" />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Panel title="Threat Volume" desc="Detected vs blocked — last 14 days" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.threat_volume}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis dataKey="day" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={ttStyle} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
              <Line type="monotone" dataKey="volume" stroke="#2563EB" strokeWidth={2} dot={false} name="Volume" />
              <Line type="monotone" dataKey="blocked" stroke="#10B981" strokeWidth={2} dot={false} name="Blocked" />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Threat Severity" desc="Distribution by risk level">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.severity} dataKey="value" nameKey="name" innerRadius={50} outerRadius={88} paddingAngle={2}>
                {data.severity.map((e) => <Cell key={e.name} fill={SEV_COLORS[e.name]} stroke="#0f172a" />)}
              </Pie>
              <Tooltip contentStyle={ttStyle} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel title="Highest Asset Risk" desc="Top assets by risk score">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.asset_risk} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
              <XAxis type="number" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} domain={[0, 100]} />
              <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={10} width={130} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={ttStyle} cursor={{ fill: "#1e293b55" }} />
              <Bar dataKey="risk_score" radius={[0, 4, 4, 0]} name="Risk score">
                {data.asset_risk.map((e, i) => <Cell key={i} fill={e.risk_score >= 90 ? "#EF4444" : e.risk_score >= 75 ? "#FB923C" : e.risk_score >= 50 ? "#F59E0B" : "#10B981"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Organization Risk Posture" desc="Security score by organization">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.org_risk}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis dataKey="name" stroke="#64748b" fontSize={9} tickLine={false} axisLine={false} interval={0} angle={-12} textAnchor="end" height={50} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} domain={[0, 100]} />
              <Tooltip contentStyle={ttStyle} cursor={{ fill: "#1e293b55" }} />
              <Bar dataKey="score" radius={[4, 4, 0, 0]} name="Security score">
                {data.org_risk.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="mt-4">
        <SocEventStream />
      </div>
    </div>
  );
}

const SOC_EVENTS = [
  ["Authorization denied — outside approved scope", "DENIED", "MEDIUM", "#fbbf24"],
  ["3/3 consensus satisfied — request allowed", "ALLOWED", "LOW", "#34d399"],
  ["Suspicious automation rate-limited", "RATE LIMITED", "MEDIUM", "#fbbf24"],
  ["Cross-tenant access attempt blocked", "BLOCKED", "HIGH", "#f87171"],
  ["AI agent self-escalation blocked", "BLOCKED", "HIGH", "#f87171"],
  ["Canary token triggered — incident opened", "ISOLATED", "CRITICAL", "#fb923c"],
  ["Hardware step-up verified — privilege granted", "ALLOWED", "LOW", "#34d399"],
  ["Replayed authorization rejected", "DENIED", "MEDIUM", "#fbbf24"],
  ["Lateral movement contained — asset isolated", "ISOLATED", "CRITICAL", "#fb923c"],
  ["Policy violation — request challenged", "CHALLENGED", "MEDIUM", "#67e8f9"],
];

function SocEventStream() {
  const [events, setEvents] = useState([]);
  const i = useRef(0);
  useEffect(() => {
    const push = () => {
      const [msg, decision, sev, tone] = SOC_EVENTS[i.current % SOC_EVENTS.length];
      i.current += 1;
      setEvents((e) => [{
        id: `${Date.now()}-${i.current}`, msg, decision, sev, tone,
        eid: `SX-EVT-${(100000 + i.current).toString().slice(1)}`,
        asset: ["asset_d6eea1ec1a", "asset_5ce68c672b", "asset_1359f170a2"][i.current % 3],
        src: ["SUSPICIOUS_AUTOMATION", "HUMAN", "AI_ASSOCIATED", "UNKNOWN"][i.current % 4],
        risk: [18, 42, 67, 88, 95][i.current % 5],
        ts: new Date().toLocaleTimeString("en-GB"),
      }, ...e].slice(0, 8));
    };
    push();
    const t = setInterval(push, 2400);
    return () => clearInterval(t);
  }, []);
  return (
    <Panel title="Live Security Event Stream" desc="DEMO / SIMULATED — decisions enter as they are evaluated" testid="soc-event-stream">
      <div className="space-y-1.5">
        <AnimatePresence initial={false}>
          {events.map((e) => (
            <motion.div key={e.id} layout
              initial={{ opacity: 0, y: -14, backgroundColor: "rgba(59,130,246,0.10)" }}
              animate={{ opacity: 1, y: 0, backgroundColor: "rgba(0,0,0,0)" }}
              exit={{ opacity: 0 }} transition={{ duration: 0.4, ease: "easeOut" }}
              className="flex items-center gap-3 rounded-lg border border-slate-800/60 px-3 py-2.5">
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: e.tone, boxShadow: `0 0 7px ${e.tone}` }} />
              <span className="hidden font-mono text-[10px] text-slate-500 sm:inline">{e.ts}</span>
              <span className="hidden font-mono text-[10px] text-slate-600 md:inline">{e.eid}</span>
              <span className="min-w-0 flex-1 truncate text-xs text-slate-300">{e.msg}</span>
              <span className="hidden font-mono text-[10px] text-slate-500 lg:inline">{e.src}</span>
              <span className="font-mono text-[10px] tabular-nums text-slate-400">risk {e.risk}</span>
              <span className="rounded px-2 py-0.5 font-mono text-[10px] font-semibold" style={{ color: e.tone, background: `${e.tone}1a` }}>{e.decision}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </Panel>
  );
}
